/* eslint-env browser */
// Driver incentive dashboard (wave 3, W2_Portal_Tables look): the period and
// qualification filters as chips with their counts, the figures as tiles,
// then every driver in the modern table ("Show details" lists the trips that
// counted, each opening its booking).
//
// Every figure keeps its formula, computed in memory from what is already
// loaded (the users list and the loaded bookings; no new reads): COMPLETE /
// PAID trips per driver in the period; 6–7 trips = bronze (100), exactly 8 =
// silver (200), more than 8 = gold (500); progress = trips / 8; qualified =
// 6 or more. The week runs Sunday to Saturday in Addis Ababa time (owner
// rule: every time is Addis Ababa). Read-only. Export (CSV and print, every
// row of the current view) as before; the phone is hidden in demo mode.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it never opens the row and
// is not there while numbers are hidden.
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { tokens as c } from '../theme';
import ClickableUserLink from '../components/ClickableUserLink';
import DashboardCard from '../components/DashboardCard';
import { ADDIS_OFFSET_MS, formatAddisDate, formatAddisDateTime } from '../common/exportUtils';
import { CopyButton, DataTable, PageHeader, ChipGroup, StatusChip, formatMoney, formatNumber, usePageState } from '../components/modern';
import { BookingStatusChip } from './BookingHistory';

const DAY_MS = 24 * 60 * 60 * 1000;
const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// Sunday 00:00 to Saturday 23:59:59.999 (Addis Ababa) of the week `ms` is in.
const addisWeek = (ms) => {
  const shifted = ms + ADDIS_OFFSET_MS;
  const dayStart = Math.floor(shifted / DAY_MS) * DAY_MS;
  const weekday = new Date(dayStart).getUTCDay(); // 0 = Sunday
  const start = dayStart - weekday * DAY_MS - ADDIS_OFFSET_MS;
  return { start, end: start + 7 * DAY_MS - 1 };
};

const TIER_TONE = { gold: 'amber', silver: 'neutral', bronze: 'warning', none: 'neutral' };

export default function DriverIncentiveDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const settings = useSelector((state) => state.settingsdata.settings);
  const usersdata = useSelector((state) => state.usersdata);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  // Kept while staff open a booking and come back (owner, 28 Oct).
  const [weekFilter, setWeekFilter] = usePageState('driver_incentives', 'week', 'current'); // current, last, all
  const [qualificationFilter, setQualificationFilter] = usePageState('driver_incentives', 'qualified', 'all'); // all, qualified, not_qualified

  // Week range for the filter (Addis Ababa weeks).
  const weekRange = useMemo(() => {
    const now = Date.now();
    if (weekFilter === 'all') return { start: null, end: null };
    if (weekFilter === 'last') return addisWeek(now - 7 * DAY_MS);
    return addisWeek(now);
  }, [weekFilter]);

  // Process driver incentive data — pre-index bookings by driver ID (O(n+m) instead of O(n*m))
  const { processedIncentiveData, driverBookingsMap } = useMemo(() => {
    if (!usersdata.users || !bookinglistdata.bookings) {
      return { processedIncentiveData: [], driverBookingsMap: {} };
    }

    const drivers = usersdata.users.filter((user) => user.usertype === 'driver');

    // Pre-index completed bookings by driver ID
    const bookingsByDriver = {};
    bookinglistdata.bookings.forEach((booking) => {
      if (booking.status === 'COMPLETE' || booking.status === 'PAID') {
        if (!bookingsByDriver[booking.driver]) bookingsByDriver[booking.driver] = [];
        bookingsByDriver[booking.driver].push(booking);
      }
    });

    const startTs = weekRange.start;
    const endTs = weekRange.end;

    const bookingsMap = {};
    const driverStats = drivers.map((driver) => {
      let driverBookings = bookingsByDriver[driver.id] || [];

      if (startTs && endTs) {
        driverBookings = driverBookings.filter((booking) => {
          if (!booking.tripdate) return false;
          const ts = new Date(booking.tripdate).getTime();
          return ts >= startTs && ts <= endTs;
        });
      }

      bookingsMap[driver.id] = driverBookings;
      const tripsCompleted = driverBookings.length;

      let incentiveTier = 'none';
      let incentiveAmount = 0;
      if (tripsCompleted > 8) {
        incentiveTier = 'gold';
        incentiveAmount = 500;
      } else if (tripsCompleted === 8) {
        incentiveTier = 'silver';
        incentiveAmount = 200;
      } else if (tripsCompleted >= 6) {
        incentiveTier = 'bronze';
        incentiveAmount = 100;
      }

      return {
        id: driver.id,
        driverName: `${driver.firstName} ${driver.lastName}`,
        mobile: driver.mobile || '',
        email: driver.email || '',
        tripsCompleted,
        targetProgress: tripsCompleted >= 8 ? 100 : (tripsCompleted / 8) * 100,
        incentiveTier,
        incentiveAmount,
        qualified: tripsCompleted >= 6,
        walletBalance: driver.walletBalance || 0,
        topUpBalance: driver.topUpBalance || 0,
      };
    });

    driverStats.sort((a, b) => b.tripsCompleted - a.tripsCompleted);
    return { processedIncentiveData: driverStats, driverBookingsMap: bookingsMap };
  }, [usersdata.users, bookinglistdata.bookings, weekRange]);

  const incentiveData = processedIncentiveData;

  // Apply qualification filter — derived data, no state needed
  const filteredIncentiveData = useMemo(() => {
    if (qualificationFilter === 'qualified') return incentiveData.filter((d) => d.qualified);
    if (qualificationFilter === 'not_qualified') return incentiveData.filter((d) => !d.qualified);
    return incentiveData;
  }, [incentiveData, qualificationFilter]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const qualified = filteredIncentiveData.filter((d) => d.qualified);
    const totalIncentive = qualified.reduce((sum, d) => sum + d.incentiveAmount, 0);
    const goldTier = filteredIncentiveData.filter((d) => d.incentiveTier === 'gold').length;
    const silverTier = filteredIncentiveData.filter((d) => d.incentiveTier === 'silver').length;
    const bronzeTier = filteredIncentiveData.filter((d) => d.incentiveTier === 'bronze').length;

    return {
      totalDrivers: incentiveData.length,
      displayedDrivers: filteredIncentiveData.length,
      qualifiedDrivers: qualified.length,
      totalIncentivePool: totalIncentive,
      goldTier,
      silverTier,
      bronzeTier,
      averageTrips: filteredIncentiveData.length > 0 ? (filteredIncentiveData.reduce((sum, d) => sum + d.tripsCompleted, 0) / filteredIncentiveData.length).toFixed(1) : 0,
    };
  }, [incentiveData, filteredIncentiveData]);

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'gold':
        return t('incentive_tier_gold_label', { defaultValue: '500 ETB (8+ trips)' });
      case 'silver':
        return t('incentive_tier_silver_label', { defaultValue: '200 ETB (8 trips)' });
      case 'bronze':
        return t('incentive_tier_bronze_label', { defaultValue: '100 ETB (6-7 trips)' });
      default:
        return t('not_qualified');
    }
  };

  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);
  const hidden = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const money = (v) => formatMoney(parseFloat(v || 0), settings, true);
  const yes = t('yes');
  const no = t('no');
  const qualifiedCount = incentiveData.filter((d) => d.qualified).length;

  const columns = [
    {
      id: 'driverName',
      title: t('driver_name'),
      field: 'driverName',
      render: (rowData) => <ClickableUserLink value={rowData.driverName} type="driver" searchField="name" userId={rowData.id} iconType="car" />,
    },
    {
      id: 'mobile',
      title: t('mobile'),
      field: 'mobile',
      nowrap: true,
      searchValue: (row) => (allowPrivate ? row.mobile : ''),
      sortValue: (row) => (allowPrivate ? row.mobile : ''),
      render: (rowData) =>
        !allowPrivate ? (
          hidden
        ) : rowData.mobile ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            <ClickableUserLink value={rowData.mobile} type="driver" searchField="phone" userId={rowData.id} iconType="phone" />
            <CopyButton value={rowData.mobile} phone what={phoneWord} sx={{ my: -0.5 }} />
          </Box>
        ) : (
          '-'
        ),
    },
    {
      id: 'tripsCompleted',
      title: t('trips_completed'),
      field: 'tripsCompleted',
      type: 'number',
      render: (rowData) => (
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ fontWeight: 700, fontSize: 15 }}>
            {rowData.tripsCompleted}
          </Box>
          <Box component="span" sx={{ color: c.TEXT_3, fontSize: 13 }}>
            {' / 8'}
          </Box>
        </Box>
      ),
    },
    {
      id: 'targetProgress',
      title: t('progress'),
      field: 'targetProgress',
      type: 'number',
      exportValue: (row) => `${Number(row.targetProgress).toFixed(0)}%`,
      render: (rowData) => {
        const value = Math.min(rowData.targetProgress, 100);
        const barColor = rowData.targetProgress >= 100 ? c.SUCCESS : rowData.targetProgress >= 75 ? c.AMBER_TEXT : c.ACCENT;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
            <LinearProgress
              variant="determinate"
              value={value}
              aria-label={`${t('progress')}: ${rowData.targetProgress.toFixed(0)}%`}
              sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: c.SURFACE_ALT, '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 4 } }}
            />
            <Box component="span" sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: 38, textAlign: 'end' }}>
              {`${rowData.targetProgress.toFixed(0)}%`}
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'incentiveTier',
      title: t('incentive_tier'),
      field: 'incentiveTier',
      searchValue: (row) => getTierLabel(row.incentiveTier),
      sortValue: (row) => row.incentiveAmount,
      exportValue: (row) => getTierLabel(row.incentiveTier),
      render: (rowData) => <StatusChip tone={TIER_TONE[rowData.incentiveTier] || 'neutral'} icon={<EmojiEventsOutlinedIcon />} label={getTierLabel(rowData.incentiveTier)} />,
    },
    {
      id: 'incentiveAmount',
      title: t('incentive_amount'),
      field: 'incentiveAmount',
      type: 'money',
      render: (rowData) =>
        rowData.incentiveAmount > 0 ? (
          <Box component="span" sx={{ fontWeight: 700, color: c.SUCCESS_TEXT }}>
            {money(rowData.incentiveAmount)}
          </Box>
        ) : (
          '-'
        ),
    },
    { id: 'topUpBalance', title: t('topup_balance'), field: 'topUpBalance', type: 'money', render: (rowData) => money(rowData.topUpBalance) },
    { id: 'walletBalance', title: t('earning_balance'), field: 'walletBalance', type: 'money', render: (rowData) => money(rowData.walletBalance) },
    {
      id: 'qualified',
      title: t('qualified'),
      field: 'qualified',
      sortValue: (row) => (row.qualified ? 1 : 0),
      exportValue: (row) => (row.qualified ? yes : no),
      render: (rowData) => (
        <StatusChip tone={rowData.qualified ? 'success' : 'neutral'} icon={rowData.qualified ? <CheckRoundedIcon /> : <RemoveRoundedIcon />} label={rowData.qualified ? yes : no} />
      ),
    },
  ];

  // The same columns in the export; the phone stays hidden in demo mode.
  const exportColumns = [
    { title: t('driver_name'), field: 'driverName' },
    { title: t('mobile'), value: (row) => (allowPrivate ? row.mobile : hidden) },
    { title: t('trips_completed'), field: 'tripsCompleted' },
    { title: t('progress'), value: (row) => `${Number(row.targetProgress).toFixed(0)}%` },
    { title: t('incentive_tier'), value: (row) => getTierLabel(row.incentiveTier) },
    { title: t('incentive_amount'), field: 'incentiveAmount', align: 'right' },
    { title: t('topup_balance'), field: 'topUpBalance', align: 'right' },
    { title: t('earning_balance'), field: 'walletBalance', align: 'right' },
    { title: t('qualified'), value: (row) => (row.qualified ? yes : no) },
  ];

  const headSx = { bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', py: 1 };
  const detailPanel = (rowData) => {
    const driverBookings = driverBookingsMap[rowData.id] || [];
    return (
      <Box sx={{ px: { xs: 1, md: 2 } }}>
        <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: c.INK, mb: 1 }}>
          {`${t('booking_details')} — ${rowData.driverName} (${driverBookings.length} ${t('trips')})`}
        </Typography>
        {driverBookings.length > 0 ? (
          <TableContainer sx={{ border: `1px solid ${c.OUTLINE}`, borderRadius: '10px', bgcolor: c.SURFACE }}>
            <Table size="small" aria-label={`${t('booking_details')}: ${rowData.driverName}`} sx={{ minWidth: 900, '& td, & th': { fontSize: 13.5, color: c.INK } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>{t('booking_id')}</TableCell>
                  <TableCell sx={headSx}>{t('date')}</TableCell>
                  <TableCell sx={headSx}>{t('customer')}</TableCell>
                  <TableCell sx={headSx}>{t('pickup')}</TableCell>
                  <TableCell sx={headSx}>{t('drop')}</TableCell>
                  <TableCell sx={{ ...headSx, textAlign: 'end' }}>{t('trip_cost')}</TableCell>
                  <TableCell sx={headSx}>{t('status')}</TableCell>
                  <TableCell sx={headSx}>{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {driverBookings.map((booking, index) => (
                  <TableRow key={booking.id || index} hover>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{booking.id?.substring(0, 8) || 'N/A'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{booking.tripdate ? formatAddisDateTime(booking.tripdate) : '—'}</TableCell>
                    <TableCell>{booking.customer_name || 'N/A'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={booking.pickupAddress || ''}>
                      {booking.pickupAddress || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={booking.dropAddress || ''}>
                      {booking.dropAddress || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'end', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {booking.trip_cost ? money(booking.trip_cost) : 'N/A'}
                    </TableCell>
                    <TableCell>{booking.status ? <BookingStatusChip status={booking.status} /> : 'N/A'}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" color="neutral" onClick={() => navigate(`/bookings/bookingdetails/${booking.id}`)} sx={{ whiteSpace: 'nowrap' }}>
                        {t('view_details')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{ fontSize: 14, color: c.INK_2, textAlign: 'center', py: 2 }}>{t('no_bookings_found')}</Typography>
        )}
      </Box>
    );
  };

  const periodOptions = [
    { value: 'current', label: t('current_week') },
    { value: 'last', label: t('last_week') },
    { value: 'all', label: t('all_time') },
  ];
  const showOptions = [
    { value: 'all', label: t('all_drivers'), count: incentiveData.length },
    { value: 'qualified', label: t('qualified'), count: qualifiedCount },
    { value: 'not_qualified', label: t('not_qualified'), count: incentiveData.length - qualifiedCount },
  ];
  const rangeText = weekRange.start && weekRange.end ? `${formatAddisDate(weekRange.start)} – ${formatAddisDate(weekRange.end)}` : t('all_time');
  const loading = (!usersdata.users || !bookinglistdata.bookings) && !!(usersdata.loading || bookinglistdata.loading);
  const title = t('driver_incentive_tracking');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader count={t('weekly_driver_incentive_tracking')} />

      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <ChipGroup label={t('time_period')} value={weekFilter} options={periodOptions} onChange={setWeekFilter} />
        <ChipGroup label={t('show')} value={qualificationFilter} options={showOptions} onChange={setQualificationFilter} />
      </Paper>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
        <DashboardCard icon={<DirectionsCarOutlinedIcon />} hue="accent" title={t('qualified_drivers')} value={formatNumber(summaryStats.qualifiedDrivers) || '0'} loading={loading} />
        <DashboardCard icon={<PaymentsOutlinedIcon />} hue="success" title={t('total_incentive_pool')} value={money(summaryStats.totalIncentivePool)} loading={loading} />
        <DashboardCard icon={<TrendingUpRoundedIcon />} hue="info" title={t('average_trips_per_driver')} value={String(summaryStats.averageTrips)} loading={loading} />
        <DashboardCard
          icon={<EmojiEventsOutlinedIcon />}
          hue="amber"
          title={t('tier_breakdown')}
          loading={loading}
          stats={[
            { label: t('incentive_tier_gold', { defaultValue: 'Gold (500 ETB)' }), value: fmt(summaryStats.goldTier) },
            { label: t('incentive_tier_silver', { defaultValue: 'Silver (200 ETB)' }), value: fmt(summaryStats.silverTier) },
            { label: t('incentive_tier_bronze', { defaultValue: 'Bronze (100 ETB)' }), value: fmt(summaryStats.bronzeTier) },
          ]}
        />
      </Box>

      <DataTable
        title={title}
        rows={filteredIncentiveData}
        columns={columns}
        loading={loading}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query || qualificationFilter !== 'all'
              ? t('table_count_shown', { shown: fmt(query ? shown : total), total: fmt(incentiveData.length), defaultValue: '{{shown}} of {{total}}' })
              : t('incentive_drivers_count', {
                  count: total,
                  n: fmt(total),
                  defaultValue_one: '{{n}} driver',
                  defaultValue_other: '{{n}} drivers',
                  defaultValue: '{{n}} drivers',
                })
        }
        subtitle={rangeText}
        resortKey={`${allowPrivate ? 'private' : 'demo'}-${weekFilter}`}
        rowLabel={(row) => row.driverName}
        detailPanel={detailPanel}
        prefsKey="driver_incentives"
        stateKey="driver_incentives"
        exportCsv
        exportPrint
        exportRows="all"
        exportColumns={exportColumns}
        exportFileName={`Driver_Incentive_${weekFilter}`}
        exportTitle={title}
        exportSubtitle={rangeText}
        minWidth={1180}
        emptyState={({ query }) =>
          query ? null : { icon: <EmojiEventsOutlinedIcon />, title: t('incentive_empty', { defaultValue: 'No drivers to show for this period' }) }
        }
      />
    </Box>
  );
}
